import React, { useEffect, useRef, useState } from "react";
import AddItemCard from "./AddItemCard";
import {
  ArrowLeft,
  Check,
  Download,
  FileCheck,
  Loader2,
  Plus,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import directHit from "@/assets/images/DirectHit.gif";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import { formatFileSize } from "@/shared/utils/helperFunctions";
import { OPTION_LABELS, UploadTypes } from "@/shared/utils/utils";
import SecondaryButton from "../Button/SecondaryButton";

const UploadModal = ({
  show,
  close = true,
  onImport,
  onClose,
  onAddContactClick,
  onRelevantAddClick,
  bgBlur = true,
}) => {
  const [optionSelected, setOptionSelected] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = "/Template.csv";
    link.download = "Template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBack = () => {
    setOptionSelected(null);
    setSelectedFile(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
    e.target.value = "";
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);

    try {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          const rows = text.split("\n");
          const headers = rows[0].split(",");
          const data = rows
            .slice(1)
            .map((row) => {
              const values = row.split(",");
              return headers.reduce((obj, header, index) => {
                obj[header.trim()] = values[index]?.trim() || "";
                return obj;
              }, {});
            })
            .filter((row) => Object.values(row).some((val) => val));

          const success = await onImport(e, data, selectedFile);
          if (success) {
            onClose();
          }
        } catch (error) {
          console.error("Error processing file:", error);
        } finally {
          setIsUploading(false);
        }
      };

      reader.onerror = () => {
        console.error("Error reading file");
        setIsUploading(false);
      };

      reader.readAsText(selectedFile);
    } catch (error) {
      console.error("Error uploading file:", error);
      setIsUploading(false);
    }
  };

  const handleManualAdd = () => {
    onAddContactClick?.();
    onClose();
  };

  const handleRelevantAdd = () => {
    onRelevantAddClick?.();
    onClose();
  };

  useEffect(() => {
    if (show) {
      setOptionSelected(null);
      setSelectedFile(null);
      setIsUploading(false);
    }
  }, [show]);

  if (!show) return null;

  const labels = optionSelected ? OPTION_LABELS[optionSelected] : null;

  return (
    <div
      className={`absolute inset-0 flex items-center justify-center z-[99999] w-full ${bgBlur ? "bg-black/15 backdrop-blur-sm" : ""}`}
    >
      <div className="drop-shadow rounded-2xl bg-white dark:bg-gray-900 border dark:border-gray-800 w-[400px] h-fit p-9 flex flex-col gap-4 items-center justify-center relative">
        {close && (
          <button
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full absolute top-3 right-3 text-gray-800 dark:text-gray-200 transition-colors"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        )}
        {/* Screen 1: All options */}
        {optionSelected === null && (
          <>
            <div className="flex flex-col space-y-2 items-center w-[95%] text-center">
              <img src={directHit} alt="direct_hit_gif" className="w-14 h-14" />
              <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                Add New Contacts
              </h3>
              <p className="text-xs font-normal text-gray-500 dark:text-gray-400">
                Import contacts using a CSV or sync with your CRM
              </p>
            </div>
            <div className="flex flex-col space-y-4 w-full">
              {UploadTypes?.map((type) => (
                <AddItemCard
                  key={type?.id || type?.name}
                  type={type}
                  setOptionSelected={
                    type?.typeof === "manual"
                      ? handleManualAdd
                      : type?.typeof === "relevant"
                        ? handleRelevantAdd
                        : setOptionSelected
                  }
                />
              ))}
            </div>
          </>
        )}

        {/* Screen 2: Option-specific content */}
        {optionSelected !== null && (
          <>
            <div className="flex flex-col space-y-2 items-center w-[95%] text-center">
              <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                {labels?.title ?? ""}
              </h3>
              <p className="text-xs font-normal text-gray-500 dark:text-gray-400">
                {labels?.description ?? ""}
              </p>
            </div>

            {/* CSV: File selected - show preview + Import */}
            {optionSelected === "csv" && selectedFile !== null && (
              <div className="flex flex-col gap-4 items-center w-full">
                <div className="flex text-xs text-secondary items-center justify-between w-full border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                  <div className="flex gap-2 items-start">
                    <div className="bg-[#207C9733] rounded-full p-2 h-8 w-8 flex items-center justify-center">
                      <FileCheck />
                    </div>
                    <div className="flex flex-col gap-0.5">
                      {selectedFile?.name}
                      <span className="text-gray-500 dark:text-gray-400 text-[10px] font-medium">
                        {formatFileSize(selectedFile?.size)}
                      </span>
                    </div>
                  </div>
                  <button
                    className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    onClick={() => setSelectedFile(null)}
                    disabled={isUploading}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <PrimaryButton
                  onClick={handleFileUpload}
                  disabled={isUploading}
                  startContent={
                    isUploading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Check size={16} />
                    )
                  }
                >
                  {isUploading ? "Importing..." : "Continue"}
                </PrimaryButton>
              </div>
            )}

            {/* CSV: No file - show file picker */}
            {optionSelected === "csv" && selectedFile === null && (
              <div className="flex flex-col gap-4 w-full items-center justify-center">
                <SecondaryButton
                  icon={<Download size={16} />}
                  onClick={handleDownload}
                  className="shadow-none w-fit"
                  title="Download CSV Template"
                />

                <div className="text-[11px] leading-relaxed text-gray-600 dark:text-gray-300 bg-secondary-light-transparent p-3 rounded-xl border border-secondary-light w-full text-left">
                  <span className="font-semibold text-secondary block mb-1">
                    Phone Formatting Guidelines:
                  </span>
                  <ul className="list-disc pl-4 space-y-0.5 text-gray-700 dark:text-gray-300">
                    <li>
                      Include a country code prefix (e.g.{" "}
                      <code className="bg-secondary-transparent px-1 py-0.5 rounded text-[10px] font-mono font-semibold text-secondary">
                        +1
                      </code>{" "}
                      or{" "}
                      <code className="bg-secondary-transparent px-1 py-0.5 rounded text-[10px] font-mono font-semibold text-secondary">
                        +91
                      </code>
                      ).
                    </li>
                    <li>
                      No characters, spaces, dashes, or brackets (e.g.{" "}
                      <code className="bg-secondary-transparent px-1 py-0.5 rounded text-[10px] font-mono font-semibold text-secondary">
                        +12345678900
                      </code>
                      ).
                    </li>
                    <li>
                      Avoid gaps and ensure length is between 7 and 15 digits.
                    </li>
                  </ul>
                </div>

                <div
                  className="border w-full border-gray-300 dark:border-gray-700 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    <span className="text-secondary font-semibold underline">
                      Click to upload
                    </span>{" "}
                    or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    CSV file (max. 10MB)
                  </p>
                </div>
              </div>
            )}

            {/* Manual: Add contact button */}
            {optionSelected === "manual" && (
              <div className="flex flex-col gap-4 w-full">
                <PrimaryButton
                  onClick={handleManualAdd}
                  startContent={<Plus size={16} />}
                >
                  Add Contact
                </PrimaryButton>
              </div>
            )}

            {/* Locked (CRM)*/}
            {optionSelected === "locked" && (
              <div className="flex flex-col gap-4 w-full items-center py-4">
                <p className="text-sm text-gray-500 text-center">
                  CRM integration is coming soon. Stay tuned!
                </p>
              </div>
            )}

            {/* Back button for Screen 2 */}
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-secondary mt-2"
            >
              <ArrowLeft size={16} />
              Back
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default UploadModal;
